import { ClassTeam } from './../pages/tourDetails/TournamentClasses/components/ClassesTag/ClassesTag';
import { ClassValues } from '../pages/tourDetails/TournamentClasses/components/ClassesTag/ClassesTag';
import { ClassFormatEnum, ClassTypeEnum } from '../types/enums';
import { TournamentCategory, User } from '../types/tournament';
import { generateTeamName } from './generateTeamName';
import { toApiTournamentCategory } from './tournamentCategoryTemplate';
import { normalizeTeamLineupDraftModeForSave } from '../constant/class';
import { getBool, getNumberBool } from './typeConverters';
import { mapMatchToClassPlayPayload } from './playTagTypeConverter';
import { IMatch } from '../pages/tourDetails/TournamentPlay/components/PlayTag/PlayTag';
import {
  toPersistedGroupStandingsTieBreakers,
  toPersistedTeamMatchupScoring,
} from './groupStandingsTieBreakers';

function persistClassStandingsRules(classValues: ClassValues) {
  const inheritTieBreakers = Boolean(classValues.useInheritedGroupStandingsTieBreakers);
  const inheritScoring = Boolean(classValues.useInheritedTeamMatchupScoring);
  return {
    groupStandingsTieBreakers: toPersistedGroupStandingsTieBreakers(
      inheritTieBreakers,
      classValues.groupStandingsTieBreakers,
    ),
    teamMatchupScoring: toPersistedTeamMatchupScoring(
      inheritScoring,
      classValues.teamMatchupScoring,
    ),
    ...(classValues.type === ClassTypeEnum.TEAMS &&
    !inheritTieBreakers &&
    (classValues.groupStandingsRulesVersion ?? 1) >= 2
      ? { groupStandingsRulesVersion: 2 }
      : classValues.groupStandingsRulesVersion != null
        ? { groupStandingsRulesVersion: classValues.groupStandingsRulesVersion }
        : {}),
  };
}

function mapClassTournamentMatchesForSave(
  matches: IMatch[] | undefined,
): ReturnType<typeof mapMatchToClassPlayPayload>[] {
  return (matches ?? []).map((match) => mapMatchToClassPlayPayload(match));
}

// TO DO: Working. Added double translation for safety. Rework after demo
export const classTagTypeConverter = (
  classValues: ClassValues,
  classCollection: Array<ClassValues>
) => {
  return {
    classes: classCollection.map((data) => {
      const isNewClass = typeof data.classId === 'string';
      const { classId, ...rest } = data;

      if (classValues.teams.length) {
        classValues.teams = classValues.teams.map(({ shuffled, ...item }) => {
          return {
            ...item,
            id: typeof item.id === 'number' ? item.id : undefined,
            seed: Number(item.seed),
          } as ClassTeam;
        });
      }

      if (classValues.tournamentCategories.length) {
        classValues.tournamentCategories =
          classValues.tournamentCategories.map(toApiTournamentCategory);
      }

      if (data.classId === classValues.classId && !isNewClass) {
        return {
          id: classId,
          name: classValues.name,
          type: classValues.type,
          isHasSubClasses: classValues.isHasSubClasses,
          isDupr: getBool(classValues.isDupr),
          requiresLicense: getBool(classValues.requiresLicense),
          requiresDuprPremium: getBool(classValues.requiresDuprPremium),
          eliminationDraw: Number(classValues.eliminationDraw),
          consolationDraw: Number(classValues.consolationDraw),
          seedingType: Number(classValues.seedingType),
          ...persistClassStandingsRules(classValues),
          devAutoFillTeamUsers: getBool(classValues.devAutoFillTeamUsers),
          minPlayers: Number(classValues.minPlayers),
          maxPlayers: Number(classValues.maxPlayers),
          minTeamRoster: Number(classValues.minTeamRoster),
          maxTeamRoster: Number(classValues.maxTeamRoster),
          keywordSurface: classValues.keywordSurface,
          keywordBall: classValues.keywordBall,
          isOnSale: getBool(classValues.isOnSale),
          isRanking: getBool(classValues.isRanking),
          rankingType: classValues.rankingType ? Number(classValues.rankingType) : null,
          numberOfGroups: Number(classValues.numberOfGroups),
          boxLeagueNumOfRounds: Number(classValues.boxLeagueNumOfRounds),
          numberPlayersToBox: Number(classValues.numberPlayersToBox),
          moveBox: classValues.moveBox,
          autoProgress: classValues.autoProgress,
          playBronzeMatch: getBool(classValues.playBronzeMatch),
          tiebreakerEnabled: getBool(classValues.tiebreakerEnabled),
          groupTiebreakerEnabled: getBool(classValues.groupTiebreakerEnabled),
          tiebreakerFormat: classValues.tiebreakerFormat
            ? Number(classValues.tiebreakerFormat)
            : null,
          teamLineupDraftMode: normalizeTeamLineupDraftModeForSave(
            Number(classValues.teamLineupDraftMode),
          ),
          teamMatchupSlotOrder: Array.isArray(classValues.teamMatchupSlotOrder)
            ? classValues.teamMatchupSlotOrder
            : null,
          format: classValues.format,
          roundScoring: getBool(classValues.roundScoring),
          price: getBool(classValues.isPaidClass) ? Number(classValues.price) : 0,
          vat:
            getBool(classValues.isPaidClass) && getBool(classValues.useVat)
              ? Number(classValues.vat)
              : 0,
          salesStatus: getBool(classValues.isOnSale) ? getBool(classValues.salesStatus) : false,
          stateOfClass: getBool(classValues.stateOfClass),
          teams: classValues.teams.map((team: any) => {
            team.users = team.users.filter((v: any, index: number) => {
              const maxLength =
                classValues.type === ClassTypeEnum.SINGLES
                  ? 1
                  : classValues.type === ClassTypeEnum.DOUBLES
                  ? 2
                  : 999;
              return index < maxLength;
            });
            if (typeof team.id === 'number') {
              return { ...team, seed: Number(team.seed) };
            } else {
              const { id, ...rest } = team;
              return { ...rest, seed: Number(rest.seed) };
            }
          }),
          tournamentCategories: classValues.tournamentCategories.map(toApiTournamentCategory),
          tournamentMatches: mapClassTournamentMatchesForSave(classValues.tournamentMatches),
        };
      }
      if (data.classId === classValues.classId && isNewClass) {
        return {
          name: classValues.name,
          type: classValues.type,
          isHasSubClasses: classValues.isHasSubClasses,
          eliminationDraw: Number(classValues.eliminationDraw),
          consolationDraw: Number(classValues.consolationDraw),
          seedingType: Number(classValues.seedingType),
          ...persistClassStandingsRules(classValues),
          devAutoFillTeamUsers: getBool(classValues.devAutoFillTeamUsers),
          minPlayers: Number(classValues.minPlayers),
          maxPlayers: Number(classValues.maxPlayers),
          minTeamRoster: Number(classValues.minTeamRoster),
          maxTeamRoster: Number(classValues.maxTeamRoster),
          numberOfGroups: Number(classValues.numberOfGroups),
          boxLeagueNumOfRounds: Number(classValues.boxLeagueNumOfRounds),
          numberPlayersToBox: Number(classValues.numberPlayersToBox),
          moveBox: classValues.moveBox,
          autoProgress: classValues.autoProgress,
          playBronzeMatch: getBool(classValues.playBronzeMatch),
          tiebreakerEnabled: getBool(classValues.tiebreakerEnabled),
          groupTiebreakerEnabled: getBool(classValues.groupTiebreakerEnabled),
          tiebreakerFormat: classValues.tiebreakerFormat
            ? Number(classValues.tiebreakerFormat)
            : null,
          teamLineupDraftMode: normalizeTeamLineupDraftModeForSave(
            Number(classValues.teamLineupDraftMode),
          ),
          teamMatchupSlotOrder: Array.isArray(classValues.teamMatchupSlotOrder)
            ? classValues.teamMatchupSlotOrder
            : null,
          keywordSurface: classValues.keywordSurface,
          keywordBall: classValues.keywordBall,
          isOnSale: getBool(classValues.isOnSale),
          isDupr: getBool(classValues.isDupr),
          requiresLicense: getBool(classValues.requiresLicense),
          requiresDuprPremium: getBool(classValues.requiresDuprPremium),
          isRanking: getBool(classValues.isRanking),
          rankingType: classValues.rankingType ? Number(classValues.rankingType) : null,
          format: classValues.format,
          roundScoring: getBool(classValues.roundScoring),
          price: getBool(classValues.isPaidClass) ? Number(classValues.price) : 0,
          vat:
            getBool(classValues.isPaidClass) && getBool(classValues.useVat)
              ? Number(classValues.vat)
              : 0,
          salesStatus: getBool(classValues.isOnSale) ? getBool(classValues.salesStatus) : false,
          stateOfClass: getBool(classValues.stateOfClass),
          teams: classValues.teams.map((team: any) => {
            team.users = team.users.filter((v: any, index: number) => {
              const maxLength =
                classValues.type === ClassTypeEnum.SINGLES
                  ? 1
                  : classValues.type === ClassTypeEnum.DOUBLES
                  ? 2
                  : 999;
              return index < maxLength;
            });
            if (typeof team.id === 'number') {
              return { ...team, seed: Number(team.seed) };
            } else {
              const { id, ...rest } = team;
              return { ...rest, seed: Number(rest.seed) };
            }
          }),
          tournamentCategories: classValues.tournamentCategories.map(toApiTournamentCategory),
          tournamentMatches: mapClassTournamentMatchesForSave(classValues.tournamentMatches),
        };
      }

      if (isNewClass) {
        return {
          ...rest,
          eliminationDraw: Number(rest.eliminationDraw),
          consolationDraw: Number(rest.consolationDraw),
          isHasSubClasses: rest.isHasSubClasses,
          minPlayers: Number(rest.minPlayers),
          maxPlayers: Number(rest.maxPlayers),
          minTeamRoster: Number(rest.minTeamRoster),
          maxTeamRoster: Number(rest.maxTeamRoster),
          price: getBool(rest.isPaidClass) ? Number(rest.price) : 0,
          vat: getBool(rest.isPaidClass) && getBool(rest.useVat) ? Number(rest.vat) : 0,
          isOnSale: getBool(rest.isOnSale),
          isRanking: getBool(rest.isRanking),
          numberOfGroups: Number(rest.numberOfGroups),
          boxLeagueNumOfRounds: Number(classValues.boxLeagueNumOfRounds),
          numberPlayersToBox: Number(classValues.numberPlayersToBox),
          moveBox: classValues.moveBox,
          autoProgress: classValues.autoProgress,
          rankingType: classValues.rankingType ? Number(classValues.rankingType) : null,
          roundScoring: getBool(rest.roundScoring),
          salesStatus: getBool(rest.salesStatus),
          isDupr: getBool(rest.isDupr),
          requiresLicense: getBool(rest.requiresLicense),
          requiresDuprPremium: getBool(rest.requiresDuprPremium),
          stateOfClass: getBool(rest.stateOfClass),
          teams:
            rest.teams?.length &&
            rest.teams.map((team: any) => {
              team.users = team.users.filter((v: any, index: number) => {
                const maxLength =
                  classValues.type === ClassTypeEnum.SINGLES
                    ? 1
                    : classValues.type === ClassTypeEnum.DOUBLES
                    ? 2
                    : 999;
                return index < maxLength;
              });
              if (typeof team.id === 'number') {
                return { ...team, seed: Number(team.seed) };
              } else {
                const { id, ...rest } = team;
                return { ...rest, seed: Number(rest.seed) };
              }
            }),
        };
      } else {
        return {
          id: classId,
          ...rest,
          minPlayers: Number(rest.minPlayers),
          maxPlayers: Number(rest.maxPlayers),
          minTeamRoster: Number(rest.minTeamRoster),
          maxTeamRoster: Number(rest.maxTeamRoster),
          isHasSubClasses: rest.isHasSubClasses,
          isRanking: getBool(rest.isRanking),
          rankingType: rest.rankingType ? Number(rest.rankingType) : null,
          price: getBool(rest.isPaidClass) ? Number(rest.price) : 0,
          vat: getBool(rest.isPaidClass) && getBool(rest.useVat) ? Number(rest.vat) : 0,
          isOnSale: getBool(rest.isOnSale),
          roundScoring: getBool(rest.roundScoring),
          numberOfGroups: Number(rest.numberOfGroups),
          boxLeagueNumOfRounds: Number(classValues.boxLeagueNumOfRounds),
          numberPlayersToBox: Number(classValues.numberPlayersToBox),
          moveBox: classValues.moveBox,
          autoProgress: classValues.autoProgress,
          salesStatus: getBool(rest.salesStatus),
          isDupr: getBool(rest.isDupr),
          requiresLicense: getBool(rest.requiresLicense),
          requiresDuprPremium: getBool(rest.requiresDuprPremium),
          stateOfClass: getBool(rest.stateOfClass),
          teams: classValues.teams.map((team: any) => {
            team.users = team.users.filter((v: any, index: number) => {
              const maxLength =
                classValues.type === ClassTypeEnum.SINGLES
                  ? 1
                  : classValues.type === ClassTypeEnum.DOUBLES
                  ? 2
                  : 999;
              return index < maxLength;
            });
            if (typeof team.id === 'number') {
              return { ...team, seed: Number(team.seed) };
            } else {
              const { id, ...rest } = team;
              return { ...rest, seed: Number(rest.seed) };
            }
          }),
        };
      }
    }),
  };
};

export const singleClassTagTypeConverter = (
  classValues: ClassValues,
  classCollection: Array<ClassValues>
) => {
  const prevClass = classCollection.find(({ classId }) => classValues.classId === classId);
  const isNewClass = typeof classValues.classId === 'string';

  const { classId, ...rest } = classValues;

  if (classValues.teams.length) {
    classValues.teams = classValues.teams.map(({ shuffled, ...item }) => {
      return {
        ...item,
        id: typeof item.id === 'number' ? item.id : undefined,
        seed: Number(item.seed),
      } as ClassTeam;
    });
  }

  let teams = classValues.teams.map((team: any) => {
    team.users = team.users.filter((v: any, index: number) => {
      const maxLength =
        classValues.type === ClassTypeEnum.SINGLES
          ? 1
          : classValues.type === ClassTypeEnum.DOUBLES
          ? 2
          : 999;
      return index < maxLength;
    });

    if (typeof team.id === 'number') {
      return { ...team, seed: Number(team.seed) };
    } else {
      const { id, ...rest } = team;
      return { ...rest, seed: Number(rest.seed) };
    }
  });

  if (
    classValues.format === ClassFormatEnum.BOX_LEAGUE ||
    classValues.format === ClassFormatEnum.BOX_LEAGUE_ELIM
  ) {
    let newUsers: number[] = [];
    
    const autoFill = getBool(classValues.devAutoFillTeamUsers);
    if (autoFill) {
      newUsers = classValues.teams.map((team) => {
        return team.users[0]?.id;
      }).filter((id): id is number => id !== undefined);
    } else {
      newUsers = classValues.teams.map((team) => team.users[0].id);
    }

    const prevSingleTeams = prevClass?.teams.filter((team) => team.users.length === 1) || [];
    // For the invalid double teams, we should check if any of the previousSingleTeam have changed user in the new teams.
    // Object that maps all the old user ids to the new user ids. So we can update any user according to the changes
    const userMap = prevSingleTeams.reduce((acc: Record<number, User>, team) => {
      const newTeam = classValues.teams.find((t) => t.id === team.id);
      acc[team.users[0].id] = newTeam?.users[0] ?? team.users[0];
      return acc;
    }, {});
    // For Box League we first need to compare the previous teams with the new ones, and if any of the users have changed, we need to change the users in the "hidden" double teams as well
    const doubleTeams = prevClass?.teams.filter((team) => team.users.length > 1) || [];

    const updatedDoubleTeams = doubleTeams.map((team) => {
      const player1 = userMap[team.users[0].id] ?? team.users[0];
      const player2 = userMap[team.users[1].id] ?? team.users[1];
      return {
        ...team,
        name: generateTeamName([player1, player2], { value: classValues.type, label: '' }, team),
        users: [player1, player2],
      };
    });

    const validDoubleTeams = updatedDoubleTeams?.filter((team) =>
      team.users.every((user) => newUsers.includes(user.id))
    );

    teams = teams.concat(validDoubleTeams);
  }

  if (!isNewClass) {
    let vat: number = 0;
    if (getBool(classValues.isPaidClass) && getBool(classValues.useVat)) {
      vat = Number(classValues.vat);
    }
    return {
      ...prevClass,
      ...rest,
      id: classId,
      name: classValues.name,
      type: classValues.type,
      isHasSubClasses: classValues.isHasSubClasses,
      eliminationDraw: Number(classValues.eliminationDraw),
      consolationDraw: Number(classValues.consolationDraw),
      seedingType: Number(classValues.seedingType),
      ...persistClassStandingsRules(classValues),
      devAutoFillTeamUsers: getBool(classValues.devAutoFillTeamUsers),
      minPlayers: Number(classValues.minPlayers),
      maxPlayers: Number(classValues.maxPlayers),
      minTeamRoster: Number(classValues.minTeamRoster),
      maxTeamRoster: Number(classValues.maxTeamRoster),
      keywordSurface: classValues.keywordSurface,
      keywordBall: classValues.keywordBall,
      isOnSale: getBool(classValues.isOnSale),
      isRanking: getBool(classValues.isRanking),
      rankingType: classValues.rankingType ? Number(classValues.rankingType) : null,
      numberOfGroups: Number(classValues.numberOfGroups),
      boxLeagueNumOfRounds: Number(classValues.boxLeagueNumOfRounds),
      numberPlayersToBox: Number(classValues.numberPlayersToBox),
      moveBox: classValues.moveBox,
      autoProgress: classValues.autoProgress,
      playBronzeMatch: getBool(classValues.playBronzeMatch),
      tiebreakerEnabled: getBool(classValues.tiebreakerEnabled),
      groupTiebreakerEnabled: getBool(classValues.groupTiebreakerEnabled),
      tiebreakerFormat: classValues.tiebreakerFormat
        ? Number(classValues.tiebreakerFormat)
        : null,
      teamLineupDraftMode: normalizeTeamLineupDraftModeForSave(
        Number(classValues.teamLineupDraftMode),
      ),
      teamMatchupSlotOrder: Array.isArray(classValues.teamMatchupSlotOrder)
        ? classValues.teamMatchupSlotOrder
        : null,
      format: classValues.format,
      roundScoring: getBool(classValues.roundScoring),
      price: getBool(classValues.isPaidClass) ? Number(classValues.price) : 0,
      vat: vat,
      salesStatus: getBool(classValues.isOnSale) ? getBool(classValues.salesStatus) : false,
      isDupr: getBool(classValues.isDupr) ? getBool(classValues.isDupr) : false,
      requiresLicense: getBool(classValues.requiresLicense)
        ? getBool(classValues.requiresLicense)
        : false,
      requiresDuprPremium: getBool(classValues.requiresDuprPremium)
        ? getBool(classValues.requiresDuprPremium)
        : false,
      stateOfClass: getBool(classValues.stateOfClass),
      teams: teams,
      tournamentCategories: classValues.tournamentCategories.map(toApiTournamentCategory),
      tournamentMatches: mapClassTournamentMatchesForSave(classValues.tournamentMatches),
      gender: classValues.gender,
      minAge: classValues.minAge ? Number(classValues.minAge) : null,
      maxAge: classValues.maxAge ? Number(classValues.maxAge) : null,
      minDupr: classValues.minDupr ? Number(classValues.minDupr) : null,
      maxDupr: classValues.maxDupr ? Number(classValues.maxDupr) : null,
      leaderboardId: classValues.leaderboardId ? Number(classValues.leaderboardId) : null,
      leaderboardFemaleId: classValues.leaderboardFemaleId ? Number(classValues.leaderboardFemaleId) : null,
    };
  } else {
    return {
      ...prevClass,
      ...rest,
      name: classValues.name,
      type: classValues.type,
      isHasSubClasses: classValues.isHasSubClasses,
      eliminationDraw: Number(classValues.eliminationDraw),
      consolationDraw: Number(classValues.consolationDraw),
      seedingType: Number(classValues.seedingType),
      ...persistClassStandingsRules(classValues),
      devAutoFillTeamUsers: getBool(classValues.devAutoFillTeamUsers),
      minPlayers: Number(classValues.minPlayers),
      maxPlayers: Number(classValues.maxPlayers),
      minTeamRoster: Number(classValues.minTeamRoster),
      maxTeamRoster: Number(classValues.maxTeamRoster),
      numberOfGroups: Number(classValues.numberOfGroups),
      boxLeagueNumOfRounds: Number(classValues.boxLeagueNumOfRounds),
      numberPlayersToBox: Number(classValues.numberPlayersToBox),
      moveBox: classValues.moveBox,
      autoProgress: classValues.autoProgress,
      playBronzeMatch: getBool(classValues.playBronzeMatch),
      tiebreakerEnabled: getBool(classValues.tiebreakerEnabled),
      groupTiebreakerEnabled: getBool(classValues.groupTiebreakerEnabled),
      tiebreakerFormat: classValues.tiebreakerFormat
        ? Number(classValues.tiebreakerFormat)
        : null,
      teamLineupDraftMode: normalizeTeamLineupDraftModeForSave(
        Number(classValues.teamLineupDraftMode),
      ),
      teamMatchupSlotOrder: Array.isArray(classValues.teamMatchupSlotOrder)
        ? classValues.teamMatchupSlotOrder
        : null,
      keywordSurface: classValues.keywordSurface,
      keywordBall: classValues.keywordBall,
      isOnSale: getBool(classValues.isOnSale),
      isRanking: getBool(classValues.isRanking),
      rankingType: classValues.rankingType ? Number(classValues.rankingType) : null,
      format: classValues.format,
      roundScoring: getBool(classValues.roundScoring),
      price: getBool(classValues.isPaidClass) ? Number(classValues.price) : 0,
      vat:
        getBool(classValues.isPaidClass) && getBool(classValues.useVat)
          ? Number(classValues.vat)
          : 0,
      salesStatus: getBool(classValues.isOnSale) ? getBool(classValues.salesStatus) : false,
      isDupr: getBool(classValues.isDupr) ? getBool(classValues.isDupr) : false,
      requiresLicense: getBool(classValues.requiresLicense)
        ? getBool(classValues.requiresLicense)
        : false,
      requiresDuprPremium: getBool(classValues.requiresDuprPremium)
        ? getBool(classValues.requiresDuprPremium)
        : false,
      stateOfClass: getBool(classValues.stateOfClass),
      teams: classValues.teams.map((team: any) => {
        team.users = team.users.filter((v: any, index: number) => {
          const maxLength =
            classValues.type === ClassTypeEnum.SINGLES
              ? 1
              : classValues.type === ClassTypeEnum.DOUBLES
              ? 2
              : 999;
          return index < maxLength;
        });
        if (typeof team.id === 'number') {
          return { ...team, seed: Number(team.seed) };
        } else {
          const { id, ...rest } = team;
          return { ...rest, seed: Number(rest.seed) };
        }
      }),
      tournamentCategories: classValues.tournamentCategories.map(toApiTournamentCategory),
      tournamentMatches: mapClassTournamentMatchesForSave(classValues.tournamentMatches),
      gender: classValues.gender,
      minAge: classValues.minAge ? Number(classValues.minAge) : undefined,
      maxAge: classValues.maxAge ? Number(classValues.maxAge) : undefined,
      minDupr: classValues.minDupr ? Number(classValues.minDupr) : undefined,
      maxDupr: classValues.maxDupr ? Number(classValues.maxDupr) : undefined,
      leaderboardId: classValues.leaderboardId ? Number(classValues.leaderboardId) : null,
      leaderboardFemaleId: classValues.leaderboardFemaleId ? Number(classValues.leaderboardFemaleId) : null,
    };
  }
};
